import React, { useRef, useState, useMemo } from "react";
import Webcam from "react-webcam";
import axios from "axios";
import styled from "styled-components";

// Layout
const Container = styled.div`
  display: grid;
  place-items: center;
  padding: 32px 16px;
  min-height: 100%;
`;

const Card = styled.div`
  width: 100%;
  max-width: 980px;
  border-radius: var(--radius-lg);
  background: linear-gradient(180deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06));
  border: 1px solid var(--card-border);
  backdrop-filter: blur(14px) saturate(140%);
  box-shadow: var(--shadow);
  overflow: hidden;
`;

const Header = styled.header`
  padding: 20px 24px;
  background: linear-gradient(135deg, rgba(99,102,241,0.25), rgba(34,211,238,0.2));
  border-bottom: 1px solid rgba(255,255,255,0.18);
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  letter-spacing: .2px;
`;

const Content = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr 1fr;
  padding: 20px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// Left: form
const Panel = styled.div`
  background: var(--card-glass);
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: var(--radius);
  padding: 18px;
`;

const PanelTitle = styled.h3`
  margin: 0 0 14px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--muted);
  letter-spacing: .4px;
`;

const Field = styled.div`
  display: grid;
  gap: 10px;
  margin-bottom: 14px;
`;

const Label = styled.label`
  font-size: 12px;
  color: var(--muted);
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: var(--radius-sm);
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.18);
  color: var(--text);
  outline: none;
  transition: var(--transition);
  &:focus {
    border-color: var(--primary);
    box-shadow: var(--ring);
  }
  &::placeholder {
    color: #9aa6b2;
  }
`;

// Right: camera panel
const CameraWrap = styled.div`
  position: relative;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.14);
  border-radius: var(--radius);
  overflow: hidden;
  aspect-ratio: 4 / 3;
  display: grid;
  place-items: center;
`;

const GlassOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(500px 380px at 10% 10%, rgba(255,255,255,0.10), transparent 60%),
              linear-gradient(180deg, transparent, rgba(0,0,0,0.12) 80%);
`;

const WebcamBox = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  overflow: hidden;
  video, img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 14px;
`;

const Button = styled.button`
  appearance: none;
  border: none;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: #fff;
  padding: 12px 20px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  &:hover { transform: translateY(-1px); }
  &:active { transform: translateY(0); }
  &:disabled {
    opacity: .5; cursor: not-allowed; transform: none;
  }
`;

const GhostButton = styled(Button)`
  background: linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.06));
`;

const DangerButton = styled(Button)`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
`;

const SuccessButton = styled(Button)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
`;

// Footer actions
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 20px 20px 20px;
`;

// Small chip
const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.16);
  font-size: 12px;
  color: var(--muted);
`;

const REQUIRED_SHOTS = 5;
const SHOT_LABELS = [
  "Look straight ahead",
  "Turn slightly left",
  "Turn slightly right",
  "Tilt head slightly up",
  "Tilt head slightly down"
];
const AUTO_RECORD_SECONDS = 5;
const AUTO_RECORD_INTERVAL_MS = 600; // ~8 candidate frames over 5 seconds

// Thumbnails for already-captured shots
const ThumbRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
`;

const ThumbWrap = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.18);
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ThumbRemove = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,0.6);
  color: #fff;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  display: grid;
  place-items: center;
`;

// Main component
export default function Register() {
  const webcamRef = useRef(null);
  const recordIntervalRef = useRef(null);
  const [form, setForm] = useState({ employee_id: "", name: "" });
  const [imgSrc, setImgSrc] = useState(null); // current preview of the shot just taken
  const [capturedShots, setCapturedShots] = useState([]); // manual mode: base64 strings, up to REQUIRED_SHOTS
  const [autoFrames, setAutoFrames] = useState([]); // auto-record mode: all sampled frames, backend picks the best
  const [recording, setRecording] = useState(false);
  const [recordSecondsLeft, setRecordSecondsLeft] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false); // checking which frames the backend will keep
  const [usedImages, setUsedImages] = useState([]); // photos the backend picked out of the recording (or last submit)
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [existingEmployee, setExistingEmployee] = useState(null);
  const [facingMode, setFacingMode] = useState("user"); // 'user' | 'environment'
  const mirrored = facingMode === "user";
  const HRbaseurl = process.env.REACT_APP_BACKEND_HR_BASE_URL;

  const videoConstraints = useMemo(() => ({
    facingMode,
    width: { ideal: 1280 },
    height: { ideal: 720 },
  }), [facingMode]);

  // Lookup employee when ID changes
  React.useEffect(() => {
    const fetchEmployee = async () => {
      const eid = form.employee_id.trim();
      if (!eid) {
        setExistingEmployee(null);
        return;
      }

      try {
        setLookupLoading(true);
        const res = await axios.get(`${HRbaseurl}employees/${eid}/`);
        setExistingEmployee(res.data);
        // Auto-fill name if it's currently empty or belongs to previous lookup
        setForm(prev => ({ ...prev, name: res.data.name }));
      } catch (err) {
        setExistingEmployee(null);
        // If not found, we don't necessarily want to clear the name if user is typing a new one
      } finally {
        setLookupLoading(false);
      }
    };

    const timer = setTimeout(fetchEmployee, 600); // debounce
    return () => clearTimeout(timer);
  }, [form.employee_id, HRbaseurl]);

  // Stop any in-progress recording if the component unmounts mid-way.
  React.useEffect(() => {
    return () => {
      if (recordIntervalRef.current) clearInterval(recordIntervalRef.current);
    };
  }, []);

  const capture = () => {
    if (!webcamRef.current) return;
    if (autoFrames.length > 0) setAutoFrames([]); // switching back to manual mode
    if (usedImages.length > 0) setUsedImages([]); // starting a fresh registration
    const shot = webcamRef.current.getScreenshot(); // base64
    if (!shot) return alert("Allow camera and ensure video is visible.");
    setImgSrc(shot);
  };

  // Samples ~8 frames over 5 seconds instead of a single click - the backend picks the
  // best 3 clear, distinct faces out of these and skips any with a missed/blurry face.
  const startAutoRecord = () => {
    if (!webcamRef.current) return;
    if (recordIntervalRef.current) clearInterval(recordIntervalRef.current);

    setCapturedShots([]);
    setImgSrc(null);
    setAutoFrames([]);
    setUsedImages([]); // starting a fresh registration
    setRecording(true);
    setRecordSecondsLeft(AUTO_RECORD_SECONDS);

    const frames = [];
    const totalTicks = Math.round((AUTO_RECORD_SECONDS * 1000) / AUTO_RECORD_INTERVAL_MS);
    let tick = 0;

    recordIntervalRef.current = setInterval(() => {
      const shot = webcamRef.current?.getScreenshot();
      if (shot) frames.push(shot);
      tick += 1;
      setRecordSecondsLeft(Math.max(0, AUTO_RECORD_SECONDS - Math.floor((tick * AUTO_RECORD_INTERVAL_MS) / 1000)));

      if (tick >= totalTicks) {
        clearInterval(recordIntervalRef.current);
        recordIntervalRef.current = null;
        setAutoFrames(frames);
        setRecording(false);
        previewSelectedFrames(frames);
      }
    }, AUTO_RECORD_INTERVAL_MS);
  };

  const cancelAutoRecord = () => {
    if (recordIntervalRef.current) {
      clearInterval(recordIntervalRef.current);
      recordIntervalRef.current = null;
    }
    setRecording(false);
    setAutoFrames([]);
    setUsedImages([]);
  };

  // Asks the backend which frames it would keep for encoding - right after the recording
  // finishes, before the user submits - so the picked photos can be shown immediately.
  const previewSelectedFrames = async (frames) => {
    if (!frames || frames.length === 0) return;
    try {
      setPreviewLoading(true);
      const res = await axios.post(
        `${HRbaseurl}preview-face-frames/`,
        { images: frames },
        { headers: { "Content-Type": "application/json" } }
      );
      const indices = res.data.selected_frame_indices;
      const used = Array.isArray(indices) ? indices.map((i) => frames[i]).filter(Boolean) : [];
      setUsedImages(used);
    } catch (err) {
      console.error("Preview error:", err);
      const msg = err.response?.data?.error || "Couldn't check the recording. Please retake.";
      alert(`⚠️ ${msg}`);
      setUsedImages([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const retake = () => setImgSrc(null);

  // Confirms the shot just taken and adds it to the set of photos to submit.
  const confirmShot = () => {
    if (!imgSrc) return;
    setCapturedShots((prev) => [...prev, imgSrc]);
    setImgSrc(null);
  };

  // Discards a previously confirmed shot so it can be retaken.
  const removeShot = (idx) => {
    setCapturedShots((prev) => prev.filter((_, i) => i !== idx));
  };

  const flipCamera = () => {
    setImgSrc(null);
    setFacingMode((m) => (m === "user" ? "environment" : "user"));
  };

  const handleApprove = async () => {
    if (!existingEmployee) return;
    try {
      setLoading(true);
      await axios.post(`${HRbaseurl}employees/${existingEmployee.employee_id}/enable_face/`);
      alert("✅ Employee approved successfully!");
      // Refresh lookup
      const res = await axios.get(`${HRbaseurl}employees/${existingEmployee.employee_id}/`);
      setExistingEmployee(res.data);
    } catch (err) {
      alert("❌ Failed to approve employee");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    // If a recording was already previewed, submit exactly the frames the backend picked
    // (fewer bytes, and guarantees the same photos get used again). Otherwise fall back to
    // the raw recording (backend will re-select) or the manual shots.
    const images = autoFrames.length > 0
      ? (usedImages.length > 0 ? usedImages : autoFrames)
      : capturedShots;
    const readyToSubmit = autoFrames.length > 0 || capturedShots.length === REQUIRED_SHOTS;

    if (!readyToSubmit) {
      return alert(`Please capture all ${REQUIRED_SHOTS} photos, or use "Record 5s" instead.`);
    }
    if (autoFrames.length > 0 && previewLoading) {
      return alert("Still checking the recording, please wait a moment.");
    }
    if (autoFrames.length > 0 && usedImages.length === 0) {
      return alert("Couldn't find a clear face in the recording. Please retake it.");
    }
    if (!form.employee_id?.trim() || !form.name?.trim()) {
      return alert("Please enter Employee ID and Name.");
    }
    try {
      setLoading(true);
      const payload = {
        employee_id: form.employee_id.trim(),
        name: form.name.trim(),
        images, // manual: exactly REQUIRED_SHOTS frames; auto-record: full batch, backend picks the best
      };
      const res = await axios.post(
        `${HRbaseurl}register/`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      // Show exactly which of the submitted photos the backend actually kept/encoded.
      const indices = res.data.selected_frame_indices;
      const used = Array.isArray(indices) && indices.length > 0
        ? indices.map((i) => images[i]).filter(Boolean)
        : images;
      setUsedImages(used);

      alert(`✅ Registered/Updated successfully: ${res.data.name}`);
      setForm({ employee_id: "", name: "" });
      setCapturedShots([]);
      setAutoFrames([]);
      setImgSrc(null);
    } catch (err) {
      console.error("Registration error:", err);
      const msg =
        err.response?.data?.error ||
        JSON.stringify(err.response?.data) ||
        "Registration failed";
      alert(`❌ ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    form.employee_id.trim() &&
    form.name.trim() &&
    !recording &&
    !previewLoading &&
    (capturedShots.length === REQUIRED_SHOTS || (autoFrames.length > 0 && usedImages.length > 0));

  return (
    <>
      <Container>
        <Card>
          <Header>
            <Title>Employee Registration / Face Update</Title>
          </Header>

          <Content>
            <Panel>
              <PanelTitle>Employee Details</PanelTitle>
              <Field>
                <Label>Employee ID</Label>
                <Input
                  placeholder="e.g. EMP00123"
                  value={form.employee_id}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                />
              </Field>
              <Field>
                <Label>Name</Label>
                <Input
                  placeholder="e.g. Ananya Sharma"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Field>
              <Chip>
                {lookupLoading ? "Checking ID..." : (
                  existingEmployee 
                    ? (existingEmployee.is_registered_face ? "Employee Registered in HR" : "Found in Global Profile")
                    : "Enter ID to Register New or Update Existing"
                )}
              </Chip>

              {existingEmployee && (
                <div style={{ marginTop: 20 }}>
                  <PanelTitle>
                    {existingEmployee.is_registered_face ? "Current Registered Image (HR)" : "Global Profile Image"}
                  </PanelTitle>
                  <CameraWrap style={{ height: 200, background: 'rgba(0,0,0,0.2)', border: existingEmployee.is_registered_face ? '2px solid var(--ok)' : '2px dashed var(--muted)' }}>
                    {existingEmployee.image_preview ? (
                      <img src={existingEmployee.image_preview} alt="Existing" style={{ objectFit: 'contain' }} />
                    ) : (
                      <div style={{ color: 'var(--muted)', fontSize: 12 }}>No image registered</div>
                    )}
                  </CameraWrap>
                  <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Chip style={{ borderColor: existingEmployee.is_active ? 'var(--ok)' : 'var(--err)', color: existingEmployee.is_active ? 'var(--ok)' : 'var(--err)' }}>
                        {existingEmployee.is_active ? "Status: Active" : "Status: Inactive"}
                      </Chip>
                    </div>
                    {!existingEmployee.is_active && existingEmployee.is_registered_face && (
                      <SuccessButton onClick={handleApprove} disabled={loading}>
                        Approve Now
                      </SuccessButton>
                    )}
                  </div>
                  {existingEmployee.message && !existingEmployee.is_registered_face && (
                    <div style={{ fontSize: '11px', color: 'var(--accent)', marginTop: '8px', fontWeight: 600 }}>
                      ℹ️ {existingEmployee.message}
                    </div>
                  )}
                </div>
              )}
            </Panel>

            <div>
              <Panel style={{ paddingBottom: 12 }}>
                <PanelTitle>
                  {autoFrames.length > 0
                    ? `Webcam — ${autoFrames.length} frames captured from recording`
                    : recording
                    ? `Webcam — Recording (${recordSecondsLeft}s left, move your head slightly)`
                    : `Webcam — Photo ${Math.min(capturedShots.length + 1, REQUIRED_SHOTS)} of ${REQUIRED_SHOTS}${
                        capturedShots.length < REQUIRED_SHOTS && !imgSrc ? `: ${SHOT_LABELS[capturedShots.length]}` : ""
                      }`}
                </PanelTitle>
                <CameraWrap>
                  <WebcamBox>
                    {autoFrames.length > 0 ? (
                      <img src={autoFrames[autoFrames.length - 1]} alt="Last recorded frame" />
                    ) : capturedShots.length >= REQUIRED_SHOTS ? (
                      <img src={capturedShots[capturedShots.length - 1]} alt="Last captured" />
                    ) : imgSrc ? (
                      <img src={imgSrc} alt="Captured" />
                    ) : (
                      <Webcam
                        ref={webcamRef}
                        audio={false}
                        mirrored={mirrored}
                        screenshotFormat="image/jpeg"
                        screenshotQuality={0.92}
                        videoConstraints={videoConstraints}
                      />
                    )}
                  </WebcamBox>
                  <GlassOverlay />
                </CameraWrap>

                <Controls>
                  {recording ? (
                    <>
                      <Chip>🎥 Recording... {recordSecondsLeft}s left</Chip>
                      <GhostButton onClick={cancelAutoRecord}>Cancel</GhostButton>
                    </>
                  ) : autoFrames.length > 0 ? (
                    <>
                      {previewLoading ? (
                        <Chip>🔍 Checking recording for clear faces...</Chip>
                      ) : usedImages.length > 0 ? (
                        <Chip>✅ Found {usedImages.length} clear photo(s) — ready to submit</Chip>
                      ) : (
                        <Chip>⚠️ No clear face found — please retake the recording</Chip>
                      )}
                      <GhostButton onClick={() => { setAutoFrames([]); setUsedImages([]); }}>Retake Recording</GhostButton>
                    </>
                  ) : capturedShots.length >= REQUIRED_SHOTS ? (
                    <Chip>✅ All {REQUIRED_SHOTS} photos captured — remove a thumbnail below to retake it</Chip>
                  ) : !imgSrc ? (
                    <>
                      <Button onClick={capture}>Capture Photo {capturedShots.length + 1}</Button>
                      <SuccessButton onClick={startAutoRecord}>🎥 Record 5s (Auto)</SuccessButton>
                      <GhostButton onClick={flipCamera}>
                        Flip Camera ({facingMode === "user" ? "Front" : "Back"})
                      </GhostButton>
                    </>
                  ) : (
                    <>
                      <SuccessButton onClick={confirmShot}>Use This Photo</SuccessButton>
                      <Button onClick={retake}>Retake</Button>
                      <GhostButton onClick={flipCamera}>
                        Flip Camera ({facingMode === "user" ? "Front" : "Back"})
                      </GhostButton>
                    </>
                  )}
                </Controls>

                {capturedShots.length > 0 && (
                  <ThumbRow>
                    {capturedShots.map((shot, idx) => (
                      <ThumbWrap key={idx}>
                        <img src={shot} alt={`Shot ${idx + 1}`} />
                        <ThumbRemove onClick={() => removeShot(idx)} title="Retake this photo">✕</ThumbRemove>
                      </ThumbWrap>
                    ))}
                  </ThumbRow>
                )}

                {usedImages.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <PanelTitle>
                      {autoFrames.length > 0
                        ? `Photos picked from the recording (${usedImages.length}) — will be used on submit`
                        : `Photos used for this registration (${usedImages.length})`}
                    </PanelTitle>
                    <ThumbRow>
                      {usedImages.map((shot, idx) => (
                        <ThumbWrap key={idx}>
                          <img src={shot} alt={`Used ${idx + 1}`} />
                        </ThumbWrap>
                      ))}
                    </ThumbRow>
                  </div>
                )}
              </Panel>
            </div>
          </Content>

          <Footer>
            <DangerButton onClick={() => {
              setForm({ employee_id: "", name: "" });
              setImgSrc(null);
              setCapturedShots([]);
              setUsedImages([]);
              cancelAutoRecord();
            }}>
              Reset
            </DangerButton>
            <SuccessButton onClick={handleRegister} disabled={!canSubmit || loading}>
              {loading ? "Processing..." : "Register / Update"}
            </SuccessButton>
          </Footer>
        </Card>
      </Container>
    </>
  );
}
